import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { categoriesService, ageGroupsService } from "../../services";
import { getAgeGroupColors } from "../../utils/ageGroupColors";
import GenericSelectTailwind from "./GenericSelectTailwind";

const CategoryFormTailwind = forwardRef(({ 
  category = null, 
  onSave, 
  onCancel, 
  isSaving = false,
  onFormValidityChange
}, ref) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    ageGroupIds: [],
    description: ""
  });
  const [ageGroups, setAgeGroups] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // Check if form is valid (for button disabling)
  const isFormValid = () => {
    if (!formData.name.trim()) return false;
    if (!formData.type) return false;
    if (formData.ageGroupIds.length === 0) return false;
    return true;
  };

  // Expose handleSubmit and isFormValid to parent
  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      if (validateForm()) {
        onSave(formData);
      }
    },
    isFormValid: isFormValid
  }));

  // Lade Altersgruppen beim ersten Laden
  useEffect(() => {
    loadAgeGroups();
  }, []);

  // Setze Formulardaten wenn Kategorie zum Bearbeiten übergeben wird
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || "",
        type: category.type || "",
        ageGroupIds: category.ageGroupIds || category.ageGroups?.map(ag => typeof ag === 'object' ? ag.id : ag) || [],
        description: category.description || ""
      });
    } else {
      // Reset form when creating new category
      setFormData({
        name: "",
        type: "",
        ageGroupIds: [],
        description: ""
      });
    }
  }, [category]);

  const loadAgeGroups = async () => {
    try {
      const response = await ageGroupsService.getAllAgeGroups();
      if (response.success) {
        // Sortiere Altersgruppen: "Alle" zuerst, dann die anderen
        const sortedAgeGroups = [...response.data].sort((a, b) => {
          if (a.name === 'Alle') return -1;
          if (b.name === 'Alle') return 1;
          return 0;
        });
        setAgeGroups(sortedAgeGroups);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Altersgruppen:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Entferne Fehler für dieses Feld
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Update form validity when form data changes
  useEffect(() => {
    if (onFormValidityChange) {
      const valid = isFormValid();
      onFormValidityChange(valid);
    }
  }, [formData.name, formData.type, formData.ageGroupIds, onFormValidityChange]);

  const handleAgeGroupToggle = (ageGroupId) => {
    setFormData(prev => {
      const ageGroup = ageGroups.find(ag => ag.id === ageGroupId);
      const isAlle = ageGroup && ageGroup.name === 'Alle';
      const isCurrentlySelected = prev.ageGroupIds.includes(ageGroupId);
      
      if (isAlle) {
        // Wenn "Alle" geklickt wurde
        if (isCurrentlySelected) {
          // "Alle" wird deaktiviert - nichts ausgewählt
          return {
            ...prev,
            ageGroupIds: []
          };
        } else {
          // "Alle" wird aktiviert - nur "Alle" ausgewählt
          return {
            ...prev,
            ageGroupIds: [ageGroupId]
          };
        }
      } else {
        // Wenn eine einzelne Altersgruppe geklickt wurde
        if (isCurrentlySelected) {
          // Altersgruppe wird deaktiviert
          return {
            ...prev,
            ageGroupIds: prev.ageGroupIds.filter(id => id !== ageGroupId)
          };
        } else {
          // Altersgruppe wird aktiviert - "Alle" muss entfernt werden, falls vorhanden
          const alleAgeGroup = ageGroups.find(ag => ag.name === 'Alle');
          const filteredIds = alleAgeGroup 
            ? prev.ageGroupIds.filter(id => id !== alleAgeGroup.id)
            : prev.ageGroupIds;
          
          return {
            ...prev,
            ageGroupIds: [...filteredIds, ageGroupId]
          };
        }
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name ist erforderlich";
    }

    if (!formData.type) {
      newErrors.type = "Typ ist erforderlich";
    }

    if (formData.ageGroupIds.length === 0) {
      newErrors.ageGroupIds = "Mindestens eine Altersgruppe muss ausgewählt werden";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const typeOptions = [
    { value: "Handform", label: "Handform" },
    { value: "Waffenform", label: "Waffenform" },
    { value: "Offene Form", label: "Offene Form" },
    { value: "Partnerform", label: "Partnerform" }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary dark:border-primary-light"></div>
        <span className="ml-3 text-sm text-slate-500 dark:text-slate-400">Lade Altersgruppen…</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* BASISINFORMATIONEN */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-visible">
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/60">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
            <i className="fas fa-info-circle text-[9px]"></i>
            Basisinformationen
          </span>
        </div>
        <div className="p-5 overflow-visible">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 h-10 border rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors ${
                  errors.name ? 'border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/20' : 'border-slate-200 dark:border-slate-600'
                }`}
                placeholder="Kategorie-Name eingeben"
              />
              {errors.name && (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle text-[10px]"></i>
                  {errors.name}
                </p>
              )}
            </div>

            {/* Typ */}
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                Typ <span className="text-red-400">*</span>
              </label>
              <GenericSelectTailwind
                value={formData.type}
                onChange={(value) => handleInputChange('type', value)}
                options={typeOptions}
                placeholder="Typ auswählen"
                leftIcon={<i className="fas fa-tag text-slate-400 dark:text-slate-500"></i>}
              />
              {errors.type && (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle text-[10px]"></i>
                  {errors.type}
                </p>
              )}
            </div>
          </div>

          {/* Beschreibung – direkt in Basisinformationen */}
          <div className="mt-4">
              <label className="block text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
              Beschreibung
              <span className="ml-1.5 text-[10px] font-normal normal-case text-slate-400 dark:text-slate-500">(optional)</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-xl text-sm text-slate-900 dark:text-slate-100 bg-slate-50 dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light transition-colors resize-none"
              placeholder="Optionale Beschreibung der Kategorie"
            />
          </div>
        </div>
      </div>

      {/* ALTERSGRUPPEN */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-visible">
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-700/60">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
              <i className="fas fa-users text-[9px]"></i>
              Altersgruppen
              <span className="text-red-400">*</span>
            </span>
            {formData.ageGroupIds.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700/40">
                <i className="fas fa-check text-[9px]"></i>
                {formData.ageGroupIds.length} gewählt
              </span>
            )}
          </div>
        </div>
        <div className="p-5 overflow-visible">
          {/* Kompakter Hinweis */}
          <div className="mb-3.5 px-3 py-2 bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700/50 rounded-xl flex items-center gap-2">
            <i className="fas fa-info-circle text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0"></i>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              <span className="font-semibold text-slate-600 dark:text-slate-300">„Alle"</span> und einzelne Altersgruppen schließen sich gegenseitig aus.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {ageGroups.map((ageGroup) => {
              const isSelected = formData.ageGroupIds.includes(ageGroup.id);
              const colors = getAgeGroupColors(ageGroup.name);
              const darkBgMap = { 'bg-emerald-100': 'dark:bg-emerald-900/30', 'bg-blue-100': 'dark:bg-blue-900/30', 'bg-violet-100': 'dark:bg-violet-900/30', 'bg-amber-100': 'dark:bg-amber-900/30', 'bg-red-100': 'dark:bg-red-900/30', 'bg-gray-100': 'dark:bg-gray-700' };
              const darkTextMap = { 'text-emerald-700': 'dark:text-emerald-300', 'text-blue-700': 'dark:text-blue-300', 'text-violet-700': 'dark:text-violet-300', 'text-amber-700': 'dark:text-amber-300', 'text-red-700': 'dark:text-red-300', 'text-gray-800': 'dark:text-gray-300' };
              const borderMap = { 'bg-emerald-100': 'border-emerald-200 dark:border-emerald-700/40', 'bg-blue-100': 'border-blue-200 dark:border-blue-700/40', 'bg-violet-100': 'border-violet-200 dark:border-violet-700/40', 'bg-amber-100': 'border-amber-200 dark:border-amber-700/40', 'bg-red-100': 'border-red-200 dark:border-red-700/40', 'bg-gray-100': 'border-gray-200 dark:border-gray-600/40' };

              return (
                <button
                  key={ageGroup.id}
                  type="button"
                  onClick={() => handleAgeGroupToggle(ageGroup.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                    isSelected
                      ? `${colors.bgLight} ${darkBgMap[colors.bgLight] || 'dark:bg-slate-600'} ${colors.text} ${darkTextMap[colors.text] || 'dark:text-slate-200'} ${borderMap[colors.bgLight] || 'border-slate-200'} shadow-sm`
                      : 'bg-white dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200'
                  }`}
                >
                  {isSelected && <i className="fas fa-check text-[9px]"></i>}
                  {ageGroup.name}
                </button>
              );
            })}
          </div>
          {errors.ageGroupIds && (
            <p className="mt-2 text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1">
              <i className="fas fa-exclamation-circle text-[10px]"></i>
              {errors.ageGroupIds}
            </p>
          )}
        </div>
      </div>
    </div>
  );
});

export default CategoryFormTailwind;
